import { GRID_CONFIG, sortByAuditUpdatedOn } from "../config";
import axios from "axios";
import Lottie from "lottie-react";
import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import {
    FaSearch,
    FaTimes,
    FaAngleDoubleLeft,
    FaAngleLeft,
    FaAngleRight,
    FaAngleDoubleRight,
} from "react-icons/fa";
import trailLoadingAnimation from "../assets/animations/trail-loading.json";

import { notifyUser } from "../utils/notifyUser";

const NorthCapital = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [selectedIssuer, setSelectedIssuer] = useState(null);
    const [issuerAccountData, setIssuerAccountData] = useState(null);
    const [issuerAccountError, setIssuerAccountError] = useState("");
    const [perPage, setPerPage] = useState(GRID_CONFIG.defaultPageSize);
    const [currentPage, setCurrentPage] = useState(1);
    const [viewedUserLoading, setViewedUserLoading] = useState(false);

    const [activeTab, setActiveTab] = useState("users");
    const [campaigns, setCampaigns] = useState([]);
    const [campaignSearch, setCampaignSearch] = useState("");
    const [campaignPerPage, setCampaignPerPage] = useState(GRID_CONFIG.defaultPageSize);
    const [campaignCurrentPage, setCampaignCurrentPage] = useState(1);
    const [showOfferingModal, setShowOfferingModal] = useState(false);
    const [selectedOffering, setSelectedOffering] = useState(null);
    const [offeringData, setOfferingData] = useState(null);
    const [offeringError, setOfferingError] = useState("");
    const [viewedOfferingLoading, setViewedOfferingLoading] = useState(false);

    const normalizeIssuerRow = (issuer) => ({
        id: issuer.issuerId || issuer.id || issuer.email,
        issuerId: issuer.issuerId || issuer.id || "",
        issuerName: issuer.issuerName || "",
        first_name: issuer.firstName || "",
        last_name: issuer.lastName || "",
        email: issuer.email || "",
        phoneNumber: issuer.phoneNumber || "",
        issuerStatus: issuer.issuerStatus || "",
        raw: issuer,
    });

    const normalizeOfferingRow = (offering) => ({
        id: offering.offeringId || offering.id || offering.issueName,
        offeringId: offering.offeringId || offering.id || "",
        issuerId: offering.issuerId || "",
        issueName: offering.issueName || offering.campaign_name || "",
        startDate: offering.startDate || "",
        endDate: offering.endDate || "",
        first_name: "",
        last_name: "",
        email: "",
        owner_email: "",
        issue_type: offering.issueType || "",
        offering_status: offering.offeringStatus || "",
        raw: offering,
    });

    const formatFieldLabel = (field) =>
        String(field || "")
            .replace(/_/g, " ")
            .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
            .replace(/\s+/g, " ")
            .trim()
            .replace(/\b\w/g, (char) => char.toUpperCase());

    const formatFieldValue = (value) => {
        if(value === null || value === undefined || value === "") {
            return "-";
        }

        if(Array.isArray(value)) {
            return value.length > 0 ? value.join(", ") : "-";
        }

        if(typeof value === "object") {
            return JSON.stringify(value, null, 2);
        }

        return String(value);
    };

    const isMeaningfulValue = (value) =>
        value !== undefined && value !== null && value !== "";

    const parseMaybeJsonObject = (value) => {
        if(value && typeof value === "object" && !Array.isArray(value)) {
            return value;
        }

        if(typeof value !== "string") {
            return null;
        }

        try {
            const parsed = JSON.parse(value);
            return parsed && typeof parsed === "object" && !Array.isArray(parsed)
                ? parsed
                : null;
        } catch {
            return null;
        }
    };

    const toArray = (value) => {
        if(Array.isArray(value)) {
            return value;
        }

        if(value && typeof value === "object") {
            return Object.values(value).filter(
                (item) => item && typeof item === "object",
            );
        }

        return [];
    };

    const getFirstAvailableValue = (source, keys) => {
        if(!source || typeof source !== "object") {
            return undefined;
        }

        return keys.reduce((found, key) => {
            if(isMeaningfulValue(found)) {
                return found;
            }

            return source[key];
        }, undefined);
    };

    const assignAliasValue = (target, source, aliases, canonicalKey) => {
        const value = getFirstAvailableValue(source, aliases);

        if(isMeaningfulValue(value)) {
            target[canonicalKey] = value;
        }
    };

    const normalizeIssuerAccountDetails = (details) => {
        const parsedDetails = parseMaybeJsonObject(details) || {};
        const normalizedDetails = { ...parsedDetails };

        assignAliasValue(
            normalizedDetails,
            parsedDetails,
            ["issuerId", "IssuerID", "issuer_id"],
            "issuerId",
        );
        assignAliasValue(
            normalizedDetails,
            parsedDetails,
            ["dob", "DOB", "dateOfBirth", "birthDate"],
            "dob",
        );
        assignAliasValue(
            normalizedDetails,
            parsedDetails,
            ["companyState", "company_state", "state", "State"],
            "companyState",
        );
        assignAliasValue(
            normalizedDetails,
            parsedDetails,
            ["issuingCountry", "issuing_country", "IssuingCountry"],
            "issuingCountry",
        );

        return normalizedDetails;
    };

    const normalizeFinancialIssuerDetails = (details) =>
        toArray(details).map((detail) => {
            const normalizedDetail = { ...detail };

            assignAliasValue(
                normalizedDetail,
                detail,
                ["funds", "Funds", "availableFunds", "balance"],
                "funds",
            );
            assignAliasValue(
                normalizedDetail,
                detail,
                ["CustodianName", "custodianName"],
                "CustodianName",
            );
            assignAliasValue(
                normalizedDetail,
                detail,
                ["AccountNumber", "accountNumber"],
                "AccountNumber",
            );

            return normalizedDetail;
        });

    const normalizeIssuerAccountResponse = (payload) => {
        const fullResponse = payload?.fullResponse || {};
        const issuerAccountDetails = normalizeIssuerAccountDetails(
            payload?.issuerAccountDetails ||
                payload?.statusDesc ||
                fullResponse.statusDesc,
        );
        const financialIssuerDetails = normalizeFinancialIssuerDetails(
            payload?.financialIssuerDetails ||
                fullResponse["Financial issuer details"],
        );

        return {
            ...payload,
            issuerId:
                payload?.issuerId ||
                issuerAccountDetails.issuerId ||
                fullResponse.issuerId ||
                "",
            issuerAccountDetails,
            financialIssuerDetails,
        };
    };

    const normalizeOfferingResponse = (payload) => {
        const offeringDetails = toArray(payload?.offeringDetails);

        return {
            ...payload,
            offeringDetails,
            primaryOfferingDetails: offeringDetails[0] || null,
        };
    };

    const normalizeOfferingDetailsResponse = (payload) => {
        const offeringPurchasedDetails = toArray(
            payload?.offeringDetails ||
                payload?.offeringPurchasedDetails ||
                payload?.fullResponse?.["Offering purchased details"],
        );

        return {
            ...payload,
            offeringPurchasedDetails,
            primaryOfferingPurchasedDetails:
                offeringPurchasedDetails[0] || null,
        };
    };

    const getDetailEntries = (details) =>
        Object.entries(details || {}).filter(([, value]) => value !== undefined);

    const closeIssuerModal = () => {
        setShowModal(false);
        setSelectedIssuer(null);
        setIssuerAccountData(null);
        setIssuerAccountError("");
        setViewedUserLoading(false);
    };

    const closeOfferingModal = () => {
        setShowOfferingModal(false);
        setSelectedOffering(null);
        setOfferingData(null);
        setOfferingError("");
        setViewedOfferingLoading(false);
    };

    const getValidSessionToken = () => {
        const sessionToken =
            localStorage.getItem("session_id") ||
            localStorage.getItem("token");

        if(!sessionToken) {
            return null;
        }

        try {
            const payload = JSON.parse(atob(sessionToken.split(".")[1]));
            const expiryMs = Number(payload?.exp) * 1000;

            if(expiryMs && Date.now() >= expiryMs) {
                localStorage.removeItem("session_id");
                return null;
            }
        } catch(error) {
            console.warn("Unable to inspect session token expiry:", error);
        }

        return sessionToken;
    };

    const getAuthRequestConfig = () => {
        const sessionToken = getValidSessionToken();

        return {
            withCredentials: true,
            headers: sessionToken
                ? { Authorization: `Bearer ${sessionToken}` }
                : undefined,
        };
    };

    const fetchIssuers = async () => {
        try {
            setLoading(true);
            const { data } = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/issuer/get-all-issuers`,
                {},
                getAuthRequestConfig(),
            );

            const issuerDetails = Array.isArray(data?.data?.issuerDetails)
                ? data.data.issuerDetails
                : [];
            setUsers(sortByAuditUpdatedOn(issuerDetails.map(normalizeIssuerRow)));
        } catch(error) {
            console.error("Failed to fetch issuers:", error);
            notifyUser(null, "error", error.response?.data?.error || "Failed to fetch issuers.", "toast", null, null, null, null, null, false, false, "top-center");
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchOfferings = async () => {
        try {
            setLoading(true);
            const { data } = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/offering/offerings`,
                {},
                getAuthRequestConfig(),
            );

            const offeringDetails = Array.isArray(data?.data?.offeringDetails)
                ? data.data.offeringDetails
                : [];
            setCampaigns(sortByAuditUpdatedOn(offeringDetails.map(normalizeOfferingRow)));
        } catch(error) {
            console.error("Failed to fetch offerings:", error);
            notifyUser(null, "error", error.response?.data?.error || "Failed to fetch offerings.", "toast", null, null, null, null, null, false, false, "top-center");
            setCampaigns([]);
        } finally {
            setLoading(false);
        }
    };

    const handleExportIssuersCSV = () => {
        const rows = filteredUsers.map((u) => ({
            IssuerID: u.issuerId || "",
            IssuerName: u.issuerName || "",
            FirstName: u.first_name || "",
            LastName: u.last_name || "",
            Email: u.email || "",
            PhoneNumber: u.phoneNumber || "",
            IssuerStatus: u.issuerStatus || "",
        }));

        if (!rows.length) {
            notifyUser(null, "info", "No data to export", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        const header = Object.keys(rows[0]);
        const csv = [
            header.join(","),
            ...rows.map((r) => header.map((h) => `"${String(r[h]).replace(/"/g, '""')}"`).join(",")),
        ].join("\n");

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "issuers_export.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleExportOfferingsCSV = () => {
        const rows = filteredCampaigns.map((c) => ({
            IssuerID: c.issuerId || "",
            OfferingID: c.offeringId || "",
            IssueName: c.issueName || "",
            StartDate: c.startDate || "",
            EndDate: c.endDate || "",
            OfferingStatus: c.offering_status || "",
        }));

        if (!rows.length) {
            notifyUser(null, "info", "No data to export", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        const header = Object.keys(rows[0]);
        const csv = [
            header.join(","),
            ...rows.map((r) => header.map((h) => `"${String(r[h]).replace(/"/g, '""')}"`).join(",")),
        ].join("\n");

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "offerings_export.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleTabChange = async (tab) => {
        setActiveTab(tab);
        if(tab === "users") {
            setCurrentPage(1);
            await fetchIssuers();
            return;
        }

        setCampaignCurrentPage(1);
        await fetchOfferings();
    };

    const handleViewCampaignDocs = async (campaign) => {
        if(!campaign || !campaign.id) {
            notifyUser(null, "error", "Invalid offering object.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        setSelectedOffering(campaign);
        setShowOfferingModal(true);
        setViewedOfferingLoading(true);
        setOfferingData(null);
        setOfferingError("");

        try {
            const [offeringResponse, offeringDetailsResponse] = await Promise.all([
                axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/api/offering/get-offering`,
                    { offeringId: campaign.offeringId },
                    getAuthRequestConfig(),
                ),
                axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/api/offering/get-offering-details`,
                    { offeringId: campaign.offeringId },
                    getAuthRequestConfig(),
                ),
            ]);

            const normalizedOffering = normalizeOfferingResponse(
                offeringResponse.data?.data,
            );
            const normalizedOfferingDetails = normalizeOfferingDetailsResponse(
                offeringDetailsResponse.data?.data,
            );

            setOfferingData({
                ...normalizedOffering,
                offeringDetailsStatusCode:
                    normalizedOfferingDetails.statusCode || "",
                offeringDetailsStatusDesc:
                    normalizedOfferingDetails.statusDesc || "",
                offeringPurchasedDetails:
                    normalizedOfferingDetails.offeringPurchasedDetails,
                primaryOfferingPurchasedDetails:
                    normalizedOfferingDetails.primaryOfferingPurchasedDetails,
                offeringDetailsFullResponse:
                    normalizedOfferingDetails.fullResponse || null,
            });
        } catch(error) {
            console.error("Failed to fetch offering:", error);
            const message =
                error.response?.data?.error ||
                error.response?.data?.message ||
                "Failed to fetch offering.";

            setOfferingError(message);
            notifyUser(null, "error", message, "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setViewedOfferingLoading(false);
        }
    };

    const handleViewUserDocs = async (user) => {
        if(!user || !user.id) {
            console.warn("handleViewUserDocs called with invalid user:", user);
            notifyUser(null, "error", "Invalid issuer selected.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        setSelectedIssuer(user);
        setShowModal(true);
        setViewedUserLoading(true);
        setIssuerAccountData(null);
        setIssuerAccountError("");

        try {
            const { data } = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/issuer/get-issuer-account`,
                { issuerId: user.issuerId },
                getAuthRequestConfig(),
            );

            setIssuerAccountData(normalizeIssuerAccountResponse(data?.data));
        } catch(error) {
            console.error("Failed to fetch issuer account:", error);
            const message =
                error.response?.data?.error ||
                error.response?.data?.message ||
                "Failed to fetch issuer account.";

            setIssuerAccountError(message);
            notifyUser(null, "error", message, "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setViewedUserLoading(false);
        }
    };

    const filteredUsers = users.filter((user) => {
        const matchesSearch =
            user.issuerId?.toLowerCase().includes(search.toLowerCase()) ||
            user.issuerName?.toLowerCase().includes(search.toLowerCase()) ||
            user.first_name?.toLowerCase().includes(search.toLowerCase()) ||
            user.last_name?.toLowerCase().includes(search.toLowerCase()) ||
            user.email?.toLowerCase().includes(search.toLowerCase()) ||
            user.phoneNumber?.toLowerCase().includes(search.toLowerCase()) ||
            user.issuerStatus?.toLowerCase().includes(search.toLowerCase());

        return matchesSearch;
    });
    const totalPages = Math.max(1, Math.ceil(filteredUsers.length / perPage));
    const paginatedUsers = filteredUsers.slice(
        (currentPage - 1) * perPage,
        currentPage * perPage,
    );

    const filteredCampaigns = campaigns.filter(
        (c) =>
            c.issueName
                ?.toLowerCase()
                .includes(campaignSearch.toLowerCase()) ||
            c.issuerId
                ?.toLowerCase()
                .includes(campaignSearch.toLowerCase()) ||
            c.offeringId
                ?.toLowerCase()
                .includes(campaignSearch.toLowerCase()) ||
            c.startDate
                ?.toLowerCase()
                .includes(campaignSearch.toLowerCase()) ||
            c.endDate
                ?.toLowerCase()
                .includes(campaignSearch.toLowerCase()) ||
            c.offering_status
                ?.toLowerCase()
                .includes(campaignSearch.toLowerCase()),
    );
    const campaignTotalPages = Math.max(1, Math.ceil(
        filteredCampaigns.length / campaignPerPage,
    ));
    const paginatedCampaigns = filteredCampaigns.slice(
        (campaignCurrentPage - 1) * campaignPerPage,
        campaignCurrentPage * campaignPerPage,
    );

    const columns = [
        { name: "Issuer ID", selector: (row) => row.issuerId, sortable: true },
        {
            name: "Issuer Name",
            selector: (row) => row.issuerName,
            sortable: true,
        },
        {
            name: "First Name",
            selector: (row) => row.first_name,
            sortable: true,
        },
        { name: "Last Name", selector: (row) => row.last_name, sortable: true },
        { name: "Email", selector: (row) => row.email, sortable: true },
        {
            name: "Phone Number",
            selector: (row) => row.phoneNumber,
            sortable: true,
        },
        {
            name: "Issuer Status",
            selector: (row) => row.issuerStatus,
            sortable: true,
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: "var(--navy)",
                color: "white",
                fontWeight: "bold",
            },
        },
        rows: {
            style: {
                cursor: "pointer",
                "&:hover": { backgroundColor: "#f1f8ff" },
            },
        },
        table: { style: { borderRadius: "8px", overflowX: "hidden" } },
    };

    const renderTabLoader = (label) => (
        <div
            className="flex flex-col items-center justify-center"
            style={{ minHeight: "56vh" }}
        >
            <Lottie
                animationData={trailLoadingAnimation}
                loop={true}
                className="w-40 h-40"
            />
            <p className="text-navy font-medium -mt-4">{label}</p>
        </div>
    );

    const [step, setStep] = useState(1);
    useEffect(() => {
        const steps = [1, 2, 3];
        let idx = 0;
        const interval = setInterval(() => {
            idx = (idx + 1) % steps.length;
            setStep(steps[idx]);
        }, 2500);
        return () => clearInterval(interval);
    }, [activeTab]);

    useEffect(() => {
        fetchIssuers();
    }, []);

    return (
        <div className="font-poppins">
            <div
                className="aqi-grid-page p-8 w-full bg-white rounded shadow"
                style={{ maxWidth: "100%" }}
            >
                <h2
                    style={{
                        color: "var(--navy)",
                        marginBottom: "20px",
                        fontSize: "large",
                        fontFamily: "large",
                    }}
                >
                    NORTH CAPITAL
                </h2>

                <div className="flex gap-4 mb-6 justify-center border-b border-[#C2D7E2] pb-4">
                    <button
                        className={`px-4 py-2 rounded font-semibold transition ${
                            activeTab === "users"
                                ? "bg-navy text-white"
                                : "bg-gray-100 text-navy"
                        }`}
                        onClick={() => handleTabChange("users")}
                    >
                        Issuers
                    </button>
                    <button
                        className={`px-4 py-2 rounded font-semibold transition ${
                            activeTab === "campaigns"
                                ? "bg-navy text-white"
                                : "bg-gray-100 text-navy"
                        }`}
                        onClick={() => handleTabChange("campaigns")}
                    >
                        Offerings
                    </button>
                </div>

                {activeTab === "users" && (
                    <>
                        {loading ? (
                            renderTabLoader("Loading issuers...")
                        ) : (
                            <>
                                <div className="aqi-grid-toolbar">
                                        <div>
                                            <select
                                                style={{
                                                    padding:
                                                        "0.375rem 2rem 0.375rem 0.75rem",
                                                    color: "var(--navy)",
                                                    backgroundColor: "#F5F7FA",
                                                    border: "2px solid #C2D7E2",
                                                    borderRadius: "0.375rem",
                                                    fontWeight: "500",
                                                    appearance: "none",
                                                    backgroundImage:
                                                        "var(--navy-select-chevron)",
                                                    backgroundRepeat: "no-repeat",
                                                    backgroundPosition:
                                                        "right 0.5rem center",
                                                    backgroundSize: "16px 12px",
                                                    transition: "all 0.2s ease",
                                                    cursor: "pointer",
                                                    outline: "none",
                                                }}
                                                className="aqi-grid-control hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                                                value={perPage}
                                                onChange={(e) => {
                                                    setPerPage(Number(e.target.value));
                                                    setCurrentPage(1);
                                                }}
                                            >
                                                {GRID_CONFIG.pageSizeOptions.map((option) => (
                                                    <option key={option} value={option}>{option}</option>
                                                ))}
                                            </select>
                                        </div>

                                    <div className="aqi-grid-search">
                                        <FaSearch
                                            className="absolute left-3 top-2.5"
                                            style={{ color: "var(--navy)" }}
                                        />
                                        <input
                                            type="text"
                                            placeholder="Search by issuer"
                                            value={search}
                                            onChange={(e) => {
                                                setSearch(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                            style={{
                                                paddingLeft: "2.5rem",
                                                paddingRight: "0.75rem",
                                                borderRadius: "0.375rem",
                                                border: "2px solid #C2D7E2",
                                                backgroundColor: "#F5F7FA",
                                                transition: "all 0.2s ease",
                                                outline: "none",
                                                color: "var(--navy)",
                                            }}
                                            className="aqi-grid-search-input hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm font-poppins"
                                        />
                                    </div>

                                    <div className="aqi-grid-toolbar-actions">
                                        <button
                                            onClick={handleExportIssuersCSV}
                                            title="Export CSV"
                                            className="aqi-grid-button aqi-grid-button--primary"
                                        >
                                            Export
                                        </button>
                                    </div>
                                </div>
                                <div
                                    style={{
                                        overflowX: "hidden",
                                        borderRadius: "6px",
                                        width: "100%",
                                        display: "block",
                                    }}
                                >
                                    <DataTable
                                        columns={columns}
                                        data={paginatedUsers}
                                        customStyles={{
                                            ...customStyles,
                                            table: {
                                                style: {
                                                    borderRadius: "8px",
                                                    minWidth: 0,
                                                    width: "100%",
                                                    tableLayout: "fixed",
                                                    overflowX: "hidden",
                                                },
                                            },
                                            headRow: {
                                                style: {
                                                    backgroundColor: "var(--navy)",
                                                    color: "white",
                                                    fontWeight: "bold",
                                                    fontSize: "13px",
                                                    minHeight: "50px",
                                                    width: "100%",
                                                },
                                            },
                                            rows: {
                                                style: {
                                                    cursor: "pointer",
                                                    minHeight: "50px",
                                                    width: "100%",
                                                    "&:hover": {
                                                        backgroundColor: "#E9F4F9",
                                                        borderLeft: "2px solid var(--navy)",
                                                    },
                                                },
                                            },
                                        }}
                                        progressPending={loading}
                                        onRowClicked={handleViewUserDocs}
                                        striped
                                        pointerOnHover
                                        noHeader
                                        highlightOnHover
                                    />
                                </div>
                                <div className="flex justify-between items-center px-2 py-3 text-sm text-gray-700 mt-4">
                                    <span>
                                        Showing {" "}
                                        {filteredUsers.length === 0
                                            ? 0
                                            : (currentPage - 1) * perPage + 1}{" "}
                                        to{" "}
                                        {Math.min(
                                            currentPage * perPage,
                                            filteredUsers.length,
                                        )}{" "}
                                        of {filteredUsers.length} issuers
                                    </span>
                                    <div className="flex gap-2 text-lg items-center">
                                        <button
                                            disabled={currentPage === 1 || loading}
                                            onClick={() => setCurrentPage(1)}
                                            className="disabled:opacity-40"
                                        >
                                            <FaAngleDoubleLeft />
                                        </button>
                                        <button
                                            disabled={currentPage === 1 || loading}
                                            onClick={() =>
                                                setCurrentPage((prev) =>
                                                    Math.max(prev - 1, 1),)
                                            }
                                            className="disabled:opacity-40"
                                        >
                                            <FaAngleLeft />
                                        </button>
                                        <span className="text-sm px-2">
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <button
                                            disabled={currentPage === totalPages || loading}
                                            onClick={() =>
                                                setCurrentPage((prev) =>
                                                    Math.min(prev + 1, totalPages),)
                                            }
                                            className="disabled:opacity-40"
                                        >
                                            <FaAngleRight />
                                        </button>
                                        <button
                                            disabled={currentPage === totalPages || loading}
                                            onClick={() => setCurrentPage(totalPages)}
                                            className="disabled:opacity-40"
                                        >
                                            <FaAngleDoubleRight />
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </>
                )}


                {activeTab === "campaigns" && (
                    <>
                        {loading ? (
                            renderTabLoader("Loading offerings...")
                        ) : campaigns.length === 0 ? (
                            <div>No approved offerings found.</div>
                        ) : (
                            <>
                                <div className="aqi-grid-toolbar">
                                        <div>
                                            <select
                                                style={{
                                                    padding:
                                                        "0.375rem 2rem 0.375rem 0.75rem",
                                                    color: "var(--navy)",
                                                    backgroundColor: "#F5F7FA",
                                                    border: "2px solid #C2D7E2",
                                                    borderRadius: "0.375rem",
                                                    fontWeight: "500",
                                                    appearance: "none",
                                                    backgroundImage:
                                                        "var(--navy-select-chevron)",
                                                    backgroundRepeat:
                                                        "no-repeat",
                                                    backgroundPosition:
                                                        "right 0.5rem center",
                                                    backgroundSize: "16px 12px",
                                                    transition: "all 0.2s ease",
                                                    cursor: "pointer",
                                                    outline: "none",
                                                }}
                                                className="aqi-grid-control hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                                                value={campaignPerPage}
                                                onChange={(e) => {
                                                    setCampaignPerPage(
                                                        Number(e.target.value),
                                                    );
                                                    setCampaignCurrentPage(1);
                                                }}
                                            >
                                                {GRID_CONFIG.pageSizeOptions.map((option) => (
                                                    <option key={option} value={option}>{option}</option>
                                                ))}
                                            </select>
                                        </div>

                                    <div className="aqi-grid-search">
                                        <FaSearch
                                            className="absolute left-3 top-2.5"
                                            style={{ color: "var(--navy)" }}
                                        />
                                        <input
                                            type="text"
                                            placeholder="Search by offering"
                                            value={campaignSearch}
                                            onChange={(e) => {
                                                setCampaignSearch(
                                                    e.target.value,
                                                );
                                                setCampaignCurrentPage(1);
                                            }}
                                            style={{
                                                paddingLeft: "2.5rem",
                                                paddingRight: "0.75rem",
                                                borderRadius: "0.375rem",
                                                border: "2px solid #C2D7E2",
                                                backgroundColor: "#F5F7FA",
                                                transition: "all 0.2s ease",
                                                outline: "none",
                                                color: "var(--navy)",
                                            }}
                                            className="aqi-grid-search-input hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm font-poppins"
                                        />
                                    </div>

                                    <div className="aqi-grid-toolbar-actions">
                                        <button
                                            onClick={handleExportOfferingsCSV}
                                            title="Export CSV"
                                            className="aqi-grid-button aqi-grid-button--primary"
                                        >
                                            Export
                                        </button>
                                    </div>
                                </div>
                                <div
                                    style={{
                                        overflowX: "hidden",
                                        borderRadius: "6px",
                                        width: "100%",
                                        display: "block",
                                    }}
                                >
                                    <DataTable
                                        columns={[
                                            {
                                                name: "Issuer ID",
                                                selector: (row) =>
                                                    row.issuerId,
                                                sortable: true,
                                            },
                                            {
                                                name: "Offering ID",
                                                selector: (row) =>
                                                    row.offeringId,
                                                sortable: true,
                                            },
                                            {
                                                name: "Issue Name",
                                                selector: (row) =>
                                                    row.issueName,
                                                sortable: true,
                                                cell: (row) => (
                                                    <span className="font-semibold text-navy">
                                                        {row.issueName}
                                                    </span>
                                                ),
                                            },
                                            {
                                                name: "Start Date",
                                                selector: (row) =>
                                                    row.startDate,
                                                sortable: true,
                                            },
                                            {
                                                name: "End Date",
                                                selector: (row) =>
                                                    row.endDate,
                                                sortable: true,
                                            },
                                            {
                                                name: "Offering Status",
                                                selector: (row) =>
                                                    row.offering_status,
                                                sortable: true,
                                            },
                                        ]}
                                        data={paginatedCampaigns}
                                        customStyles={{
                                            ...customStyles,
                                            table: {
                                                style: {
                                                    borderRadius: "8px",
                                                    minWidth: 0,
                                                    width: "100%",
                                                    tableLayout: "fixed",
                                                    overflowX: "hidden",
                                                },
                                            },
                                            headRow: {
                                                style: {
                                                    backgroundColor: "var(--navy)",
                                                    color: "white",
                                                    fontWeight: "bold",
                                                    fontSize: "13px",
                                                    minHeight: "50px",
                                                    width: "100%",
                                                },
                                            },
                                            rows: {
                                                style: {
                                                    cursor: "pointer",
                                                    minHeight: "50px",
                                                    width: "100%",
                                                    "&:hover": {
                                                        backgroundColor:
                                                            "#E9F4F9",
                                                        borderLeft:
                                                            "2px solid var(--navy)",
                                                    },
                                                },
                                            },
                                        }}
                                        progressPending={loading}
                                        onRowClicked={handleViewCampaignDocs}
                                        striped
                                        pointerOnHover
                                        noHeader
                                        highlightOnHover
                                    />
                                </div>

                                <div className="flex justify-between items-center px-2 py-3 text-sm text-gray-700 mt-4">
                                    <span>
                                        Showing {" "}
                                        {filteredCampaigns.length === 0
                                            ? 0
                                            : (campaignCurrentPage - 1) *
                                                  campaignPerPage +
                                              1}{" "}
                                        to{" "}
                                        {Math.min(
                                            campaignCurrentPage *
                                                campaignPerPage,
                                            filteredCampaigns.length,
                                        )}{" "}
                                        of {filteredCampaigns.length} offerings
                                    </span>
                                    <div className="flex gap-2 text-lg items-center">
                                        <button
                                            disabled={
                                                campaignCurrentPage === 1 ||
                                                loading
                                            }
                                            onClick={() =>
                                                setCampaignCurrentPage(1)
                                            }
                                            className="disabled:opacity-40"
                                        >
                                            <FaAngleDoubleLeft />
                                        </button>
                                        <button
                                            disabled={
                                                campaignCurrentPage === 1 ||
                                                loading
                                            }
                                            onClick={() =>
                                                setCampaignCurrentPage((prev) =>
                                                    Math.max(prev - 1, 1),
                                                )
                                            }
                                            className="disabled:opacity-40"
                                        >
                                            <FaAngleLeft />
                                        </button>
                                        <span className="text-sm px-2">
                                            Page {campaignCurrentPage} of{" "}
                                            {campaignTotalPages}
                                        </span>
                                        <button
                                            disabled={
                                                campaignCurrentPage ===
                                                    campaignTotalPages ||
                                                loading
                                            }
                                            onClick={() =>
                                                setCampaignCurrentPage((prev) =>
                                                    Math.min(
                                                        prev + 1,
                                                        campaignTotalPages,
                                                    ),
                                                )
                                            }
                                            className="disabled:opacity-40"
                                        >
                                            <FaAngleRight />
                                        </button>
                                        <button
                                            disabled={
                                                campaignCurrentPage ===
                                                    campaignTotalPages ||
                                                loading
                                            }
                                            onClick={() =>
                                                setCampaignCurrentPage(
                                                    campaignTotalPages,
                                                )
                                            }
                                            className="disabled:opacity-40"
                                        >
                                            <FaAngleDoubleRight />
                                        </button>
                                    </div>
                                </div>

                            </>
                        )}
                    </>
                )}

                {showModal && selectedIssuer && (
                    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center px-4 py-6">
                        <div className="bg-white rounded-lg w-full max-w-5xl p-6 relative max-h-[90vh] overflow-y-auto shadow-2xl">
                            <h3 className="text-xl font-bold mb-4 text-navy">
                                Issuer Account Details
                            </h3>

                            {viewedUserLoading ? (
                                <div className="flex flex-col items-center justify-center py-10">
                                    <Lottie
                                        animationData={trailLoadingAnimation}
                                        loop={true}
                                        className="w-32 h-32"
                                    />
                                    <p className="text-navy font-medium -mt-3">
                                        Loading issuer account...
                                    </p>
                                </div>
                            ) : (
                                <>
                                    {issuerAccountError && (
                                        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
                                            {issuerAccountError}
                                        </div>
                                    )}

                                    <section className="mb-6">
                                        <h4 className="font-medium text-navy text-lg border-b pb-1 mb-3">
                                            Issuer Summary
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3 text-sm">
                                            {[
                                                ["Issuer ID", selectedIssuer.issuerId],
                                                ["Issuer Name", selectedIssuer.issuerName],
                                                ["First Name", selectedIssuer.first_name],
                                                ["Last Name", selectedIssuer.last_name],
                                                ["Email", selectedIssuer.email],
                                                ["Phone Number", selectedIssuer.phoneNumber],
                                                ["Issuer Status", selectedIssuer.issuerStatus],
                                                [
                                                    "Account Lookup Status",
                                                    issuerAccountData?.statusCode || "-",
                                                ],
                                            ].map(([label, value]) => (
                                                <p
                                                    key={label}
                                                    className="text-gray-800 break-words whitespace-pre-wrap"
                                                >
                                                    <strong className="text-[#23384d]">
                                                        {label}:
                                                    </strong>{" "}
                                                    <span className="text-gray-700">
                                                        {formatFieldValue(value)}
                                                    </span>
                                                </p>
                                            ))}
                                        </div>
                                    </section>

                                    <section className="mb-6">
                                        <h4 className="font-medium text-navy text-lg border-b pb-1 mb-3">
                                            Account Profile
                                        </h4>
                                        {getDetailEntries(
                                            issuerAccountData?.issuerAccountDetails,
                                        ).length > 0 ? (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3 text-sm">
                                                {getDetailEntries(
                                                    issuerAccountData?.issuerAccountDetails,
                                                ).map(([key, value]) => (
                                                    <p
                                                        key={key}
                                                        className="text-gray-800 break-words whitespace-pre-wrap"
                                                    >
                                                        <strong className="text-[#23384d]">
                                                            {formatFieldLabel(key)}
                                                            :
                                                        </strong>{" "}
                                                        <span className="text-gray-700">
                                                            {formatFieldValue(value)}
                                                        </span>
                                                    </p>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-sm text-gray-500 italic">
                                                No account profile details returned.
                                            </div>
                                        )}
                                    </section>

                                    <section>
                                        <h4 className="font-medium text-navy text-lg border-b pb-1 mb-3">
                                            Financial Issuer Details
                                        </h4>
                                        {Array.isArray(
                                            issuerAccountData?.financialIssuerDetails,
                                        ) &&
                                        issuerAccountData.financialIssuerDetails
                                            .length > 0 ? (
                                            <div className="space-y-5">
                                                {issuerAccountData.financialIssuerDetails.map(
                                                    (detail, index) => (
                                                        <div
                                                            key={`${selectedIssuer.issuerId}-${index}`}
                                                        >
                                                            <div className="text-sm font-semibold text-navy mb-2">
                                                                Account {index + 1}
                                                            </div>
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3 text-sm border-t pt-3">
                                                                {getDetailEntries(detail).map(
                                                                    ([key, value]) => (
                                                                        <p
                                                                            key={`${index}-${key}`}
                                                                            className="text-gray-800 break-words whitespace-pre-wrap"
                                                                        >
                                                                            <strong className="text-[#23384d]">
                                                                                {formatFieldLabel(
                                                                                    key,
                                                                                )}
                                                                                :
                                                                            </strong>{" "}
                                                                            <span className="text-gray-700">
                                                                                {formatFieldValue(
                                                                                    value,
                                                                                )}
                                                                            </span>
                                                                        </p>
                                                                    ),
                                                                )}
                                                            </div>
                                                        </div>
                                                    ),
                                                )}
                                            </div>
                                        ) : (
                                            <div className="text-sm text-gray-500 italic">
                                                No financial issuer details returned.
                                            </div>
                                        )}
                                    </section>


                                </>
                            )}

                            <button
                                className="absolute top-3 right-3 text-gray-600 hover:text-red-500 text-xl"
                                onClick={closeIssuerModal}
                                aria-label="Close issuer account modal"
                            >
                                <FaTimes />
                            </button>
                        </div>
                    </div>
                )}

                {showOfferingModal && selectedOffering && (
                    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center px-4 py-6">
                        <div className="bg-white rounded-lg w-full max-w-5xl p-6 relative max-h-[90vh] overflow-y-auto shadow-2xl">
                            <h3 className="text-xl font-bold mb-4 text-navy">
                                Offering Details
                            </h3>

                            {viewedOfferingLoading ? (
                                <div className="flex flex-col items-center justify-center py-10">
                                    <Lottie
                                        animationData={trailLoadingAnimation}
                                        loop={true}
                                        className="w-32 h-32"
                                    />
                                    <p className="text-navy font-medium -mt-3">
                                        Loading offering...
                                    </p>
                                </div>
                            ) : (
                                <>
                                    {offeringError && (
                                        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
                                            {offeringError}
                                        </div>
                                    )}

                                    <section className="mb-6">
                                        <h4 className="font-medium text-navy text-lg border-b pb-1 mb-3">
                                            Offering Summary
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3 text-sm">
                                            {[
                                                ["Offering ID", selectedOffering.offeringId],
                                                ["Issuer ID", selectedOffering.issuerId],
                                                ["Issue Name", selectedOffering.issueName],
                                                ["Issue Type", selectedOffering.issue_type],
                                                ["Start Date", selectedOffering.startDate],
                                                ["End Date", selectedOffering.endDate],
                                                [
                                                    "Offering Status",
                                                    selectedOffering.offering_status,
                                                ],
                                                [
                                                    "Lookup Status",
                                                    offeringData?.statusCode || "-",
                                                ],
                                            ].map(([label, value]) => (
                                                <p
                                                    key={label}
                                                    className="text-gray-800 break-words whitespace-pre-wrap"
                                                >
                                                    <strong className="text-[#23384d]">
                                                        {label}:
                                                    </strong>{" "}
                                                    <span className="text-gray-700">
                                                        {formatFieldValue(value)}
                                                    </span>
                                                </p>
                                            ))}
                                        </div>
                                    </section>

                                    <section className="mb-6">
                                        <h4 className="font-medium text-navy text-lg border-b pb-1 mb-3">
                                            Offering Profile
                                        </h4>
                                        {getDetailEntries(
                                            offeringData?.primaryOfferingDetails,
                                        ).length > 0 ? (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3 text-sm">
                                                {getDetailEntries(
                                                    offeringData?.primaryOfferingDetails,
                                                ).map(([key, value]) => (
                                                    <p
                                                        key={key}
                                                        className="text-gray-800 break-words whitespace-pre-wrap"
                                                    >
                                                        <strong className="text-[#23384d]">
                                                            {formatFieldLabel(key)}:
                                                        </strong>{" "}
                                                        <span className="text-gray-700">
                                                            {formatFieldValue(value)}
                                                        </span>
                                                    </p>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-sm text-gray-500 italic">
                                                No offering details returned.
                                            </div>
                                        )}
                                    </section>

                                    <section className="mb-6">
                                        <h4 className="font-medium text-navy text-lg border-b pb-1 mb-3">
                                            Offering Purchased Details
                                        </h4>
                                        {getDetailEntries(
                                            offeringData?.primaryOfferingPurchasedDetails,
                                        ).length > 0 ? (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3 text-sm">
                                                {[
                                                    [
                                                        "Purchased Details Lookup Status",
                                                        offeringData?.offeringDetailsStatusCode || "-",
                                                    ],
                                                    ...getDetailEntries(
                                                        offeringData?.primaryOfferingPurchasedDetails,
                                                    ),
                                                ].map((entry) => {
                                                    const [key, value] = entry;
                                                    return (
                                                        <p
                                                            key={key}
                                                            className="text-gray-800 break-words whitespace-pre-wrap"
                                                        >
                                                            <strong className="text-[#23384d]">
                                                                {formatFieldLabel(key)}:
                                                            </strong>{" "}
                                                            <span className="text-gray-700">
                                                                {formatFieldValue(value)}
                                                            </span>
                                                        </p>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div className="text-sm text-gray-500 italic">
                                                No offering purchased details returned.
                                            </div>
                                        )}
                                    </section>

                                    {Array.isArray(offeringData?.offeringDetails) &&
                                        offeringData.offeringDetails.length > 1 && (
                                            <section>
                                                <h4 className="font-medium text-navy text-lg border-b pb-1 mb-3">
                                                    Additional Offering Records
                                                </h4>
                                                <div className="space-y-5">
                                                    {offeringData.offeringDetails.map(
                                                        (detail, index) => (
                                                            <div
                                                                key={`${selectedOffering.offeringId}-${index}`}
                                                            >
                                                                <div className="text-sm font-semibold text-navy mb-2">
                                                                    Record {index + 1}
                                                                </div>
                                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3 text-sm border-t pt-3">
                                                                    {getDetailEntries(detail).map(
                                                                        ([key, value]) => (
                                                                            <p
                                                                                key={`${index}-${key}`}
                                                                                className="text-gray-800 break-words whitespace-pre-wrap"
                                                                            >
                                                                                <strong className="text-[#23384d]">
                                                                                    {formatFieldLabel(
                                                                                        key,
                                                                                    )}
                                                                                    :
                                                                                </strong>{" "}
                                                                                <span className="text-gray-700">
                                                                                    {formatFieldValue(
                                                                                        value,
                                                                                    )}
                                                                                </span>
                                                                            </p>
                                                                        ),
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ),
                                                    )}
                                                </div>
                                            </section>
                                        )}
                                </>
                            )}

                            <button
                                className="absolute top-3 right-3 text-gray-600 hover:text-red-500 text-xl"
                                onClick={closeOfferingModal}
                                aria-label="Close offering modal"
                            >
                                <FaTimes />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NorthCapital;
